<template>
  <div class="video-container" ref="videoContainer">
    <div class="circle-container" :style="{ borderColor: primaryColor }" ref="circleContainer">
      <div class="circle-container-img" :class="{'clear': !isVideoDisabled}">
        <img
          class="lazyload"
          :src="profileImageUrl"
          :data-src="profileImageUrl"
        >
      </div>
    </div>
    <transition name="fade">
      <div
        ref="hostLabel"
        v-show="isHost"
        class="host-label"
        :style="{ backgroundColor: primaryColor }"
      >
        Host
      </div>
    </transition>
    <transition name="fade">
      <div
        ref="playerUsernameLabel"
        v-show="isGameInWaitingState && !!playerUsername && playerUsername.length > 0"
        class="player-username-label"
        :style="{ backgroundColor: primaryColor }"
      >
        {{ playerUsername }}
      </div>
    </transition>
    <transition name="pop-from-top">
      <div
        ref="answeredCheckMark"
        v-show="hasParticipantAnswered"
        class="video-check-mark"
        :style="{ color: primaryColor }"
      >
        <font-awesome-icon :icon="['fas', 'check-circle']" />
      </div>
    </transition>
    <transition name="pop-from-top">
      <div
        ref="votedCheckMark"
        v-show="hasParticipantVoted"
        class="video-check-mark"
        :style="{ color: primaryColor }"
      >
        <font-awesome-icon :icon="['fas', 'check-circle']" />
      </div>
    </transition>
    <div
      v-show="isVideoDisabled"
      class="video-mute-icon"
    >
      <font-awesome-icon :icon="['fas', 'video-slash']" />
    </div>
    <div
      v-show="isAudioDisabled"
      class="audio-mute-icon"
    >
      <font-awesome-icon :icon="['fas', 'microphone-slash']" />
    </div>
    <div
      v-show="displayPlayButton"
      class="play-button"
      @click="playMediaElements"
    >
      Click here to enable {{ playerUsername }}'s video
    </div>
  </div>
</template>

<script>
export default {
  name: 'RemoteVideoComponent',
  props: {
    gameState: Object,
    participant: Object,   // participant.identity is the auth0UserId
    isGameInWaitingState: Boolean
  },
  data: () => ({
    primaryColor: '',
    secondaryColor: '',
    backgroundColor: '',
    isAudioDisabled: false,
    isVideoDisabled: false,
    displayPlayButton: false,
    profileImageUrl: ''
  }),
  computed: {
    idToken: function () {
      return this.$auth.idToken;
    },
    isHost: function () {
      if ('hostUserId' in this.gameState) {
        if (this.gameState.hostUserId === this.participant.identity) {
          return 'gameStatus' in this.gameState &&
                 (this.gameState.gameStatus === 'waitingForPlayers' ||
                 this.gameState.gameStatus === 'waitingToStart');
        }
      }
      return false;
    },
    playerUsername: function () {
      if ('playerUsernames' in this.gameState && 'playerUserIds' in this.gameState) {
        const playerIndex = this.gameState.playerUserIds.findIndex(userId => userId === this.participant.identity);
        return this.gameState.playerUsernames[playerIndex];
      }
      return '';
    },
    hasParticipantAnswered: function() {
      if ('hasAnswered' in this.participant) {
        return this.participant.hasAnswered;
      }
      return false;
    },
    hasParticipantVoted: function() {
      if ('hasVoted' in this.participant) {
        return this.participant.hasVoted;
      }
      return false;
    }
  },
  methods: {
    async getProfileImage() {
      console.log('participant:', this.participant);
      const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/getprofileinfoforuser`, {
        auth0UserId: this.participant.identity
      }, {
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${this.idToken}`
        }
      });

      this.profileImageUrl = response.data.profileInfo.profileImageUrl;
    },
    attachTrack (track) {
      const mediaElement = track.attach();
      mediaElement.autoplay = false;

      if (track.kind === 'video') {
        mediaElement.muted = true;
        this.isVideoDisabled = !track.isEnabled;
      }

      // this.$refs.circleContainer.appendChild(mediaElement);
      this.$refs.circleContainer.insertBefore(mediaElement, this.$refs.circleContainer.childNodes[0]);

      mediaElement.play().then(() => {
        console.log('Successfully autoplayed video!');
      }).catch(error => {
        if (track.kind === 'video') {
          console.log('Autoplay video error:', error);
        } else if (track.kind === 'audio') {
          console.log('Autoplay audio error:', error);
        } else {
          console.log('Autoplay media error:', error);
        }

        this.displayPlayButton = true;
      });

      track.on('disabled', () => {
        if (track.kind === 'audio') {
          this.isAudioDisabled = true;
        } else if (track.kind === 'video') {
          this.isVideoDisabled = true;
        }
      });

      track.on('enabled', () => {
        if (track.kind === 'audio') {
          this.isAudioDisabled = false;
        } else if (track.kind === 'video') {
          this.isVideoDisabled = false;
        }
      });
    },
    async playMediaElements () {
      const promiseArray = [];

      this.$refs.circleContainer.children.forEach(child => {
        if (child instanceof HTMLMediaElement) {
          if (child.paused) {
            promiseArray.push(child.play());
          }
        }
      });

      await Promise.all(promiseArray).then(() => {
        this.displayPlayButton = false;
      }).catch(error => {
        console.log('Autoplay media error:', error);
      });
    },
    setPlayerColors () {
      const playerIndex = this.gameState.playerUserIds.findIndex(userId => userId == this.participant.identity);
      this.primaryColor = this.gameState.playerPrimaryColors[playerIndex];
      this.secondaryColor = this.gameState.playerSecondaryColors[playerIndex];
      this.backgroundColor = this.gameState.playerBackgroundColors[playerIndex];
    },
    resizeToSquare () {
      const circleDiameter = Math.min(this.$refs.videoContainer.clientWidth, this.$refs.videoContainer.clientHeight);
      const checkMarkDiameter = 0.2 * circleDiameter;
      this.$refs.circleContainer.style.height = circleDiameter + 'px';
      this.$refs.circleContainer.style.width = circleDiameter + 'px';
      const cornerRatio = 0.70710678;  // == 1 / Math.sqrt(2)

      this.$refs.answeredCheckMark.style.bottom = 'calc(50% - ' + cornerRatio * 0.5 * circleDiameter + 'px - ' + 0.5 * checkMarkDiameter + 'px)';
      this.$refs.answeredCheckMark.style.left = 'calc(50% - ' + cornerRatio * 0.5 * circleDiameter + 'px - ' + 0.5 * checkMarkDiameter + 'px)';
      this.$refs.answeredCheckMark.style.fontSize = checkMarkDiameter + 'px';

      this.$refs.votedCheckMark.style.bottom = 'calc(50% - ' + cornerRatio * 0.5 * circleDiameter + 'px - ' + 0.5 * checkMarkDiameter + 'px)';
      this.$refs.votedCheckMark.style.left = 'calc(50% - ' + cornerRatio * 0.5 * circleDiameter + 'px - ' + 0.5 * checkMarkDiameter + 'px)';
      this.$refs.votedCheckMark.style.fontSize = checkMarkDiameter + 'px';

      this.$refs.hostLabel.style.top = 'calc(50% - ' + 0.5 * circleDiameter + 'px)';
      this.$refs.hostLabel.style.fontSize = 0.5 * checkMarkDiameter + 'px';

      this.$refs.playerUsernameLabel.style.bottom = 'calc(50% - ' + 0.5 * circleDiameter + 'px)';
      this.$refs.playerUsernameLabel.style.fontSize = 0.5 * checkMarkDiameter + 'px';
    }
  },
  watch: {
    gameState: function () {
      this.setPlayerColors();
    },
    hasParticipantAnswered: function(value) {
      if (value) {
        this.$emit('play-sound-effect', 'player-submitted-prompts-' + this.participant.joinedIndex);
      }
    },
    hasParticipantVoted: function(value) {
      if (value) {
        this.$emit('play-sound-effect', 'player-submitted-prompts-' + this.participant.joinedIndex);
      }
    }
  },
  created: async function () {
    await this.getProfileImage();
  },
  mounted: async function() {
    console.log('particiants:', this.participant);
    console.log('tracks:', this.participant.tracks);
    this.$nextTick(function() {
      this.resizeToSquare();
      window.addEventListener('resize', this.resizeToSquare);
      this.setPlayerColors();

      this.participant.tracks.forEach(publication => {
        if (publication.track) {
          this.attachTrack(publication.track);
        }
      });

      this.participant.on('trackSubscribed', track => {
        this.attachTrack(track);
      });

      this.$emit('play-sound-effect', 'player-joined-lobby-' + this.participant.joinedIndex);
    });
  },
  destroyed: function() {
    window.removeEventListener('resize', this.resizeToSquare);
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/video-component-styles.scss';
</style>
